import { Params } from '@angular/router';
import { params } from '@app/configurations';
import { MetricType, SubFilter, SubFilterOption } from '../models/common.models';
import {
  AllMetrics,
  currencyMetrics,
  DelayMappedMetrics,
  LessThan7DaysMappedMetrics,
  numberMetrics,
  percentMetrics,
} from '../models/metrics.constants';
import { campaignFilters, IMetricsSubFilter } from '../models/sub-filters.model';
import { isGermanLocale } from '../services/metrics-calculations.service';

import { GlobalFilterDefaults } from '../store/states/global-filters/global-filters-state.models';
import { GetDateRangeWithoutTimeZone } from './date.utilities';
import { snakeToCamelCase } from './string.utilities';

export type MetricTypeInfo = { type: MetricType; key: AllMetrics };

export enum RefinerKeys {
  EMPTY = '',
  SOURCE = 'source',
  DESTINATION = 'destination',
  TARGET = 'target',
}
type URLs = [string, string][];
type refiners = { filterValues: Params; searchValues: Params; sortValues?: Params };

export const CompareRefiners = {
  campaignsCount: {
    EMPTY: [
      { key: '_le', value: 0 },
      { key: '_ge', value: 0 },
    ],
    ACTIVE: [{ key: '_ge', value: 1 }],
  },
};

export const splitCompositeSubFilter = (param: string) => [param.split(':')[0], param.split(':').slice(1).join(':')];

export const createComposite = (...params: string[]) => params.join(':');

export const getFilterAndSearchRefinersFromCurrentState = (
  state: Record<string, string>,
  columnsMappings: {},
  defaults: GlobalFilterDefaults,
  config,
): any => {
  if (config.includeAdGroup == null) config.includeAdGroup = true;
  if (config.includeCriteria == null) config.includeCriteria = true;
  if (config.forApiMutator == null) config.forApiMutator = false;
  if (config.xAccount == null) config.xAccount = true;
  config.refinerKeys ??= [RefinerKeys.EMPTY];

  const uri: URLs = [];
  const {
    accountsDefaultFilter,
    PGDefaultFilter,
    campaignsDefaultFilter,
    adGroupsDefaultFilter,
    criteriaDefaultFilter,
  } = defaults;
  /* #region account */
  if (config.xAccount) {
    if (typeof state[params.ADSPERT_ID] !== 'string' && typeof state[params.GLOBAL_FILTER_ACCOUNT_QUERY] !== 'string')
      uri.push(...extractAccountRefiners(accountsDefaultFilter));
    else {
      if (state[params.ADSPERT_ID]) uri.push(['filter_adspert_account_id', state[params.ADSPERT_ID]]);
      else if (state[params.GLOBAL_FILTER_ACCOUNT_QUERY])
        uri.push(...extractAccountRefiners(state[params.GLOBAL_FILTER_ACCOUNT_QUERY]));
    }
  }
  /* #endregion */

  /* #region PGs */
  let pgId = state[params.GLOBAL_FILTER_PERFORMANCE_GROUP_ID];

  if (Number.isNaN(+pgId)) {
    pgId = null;
  }

  uri.push(
    ...getFilterAndSearchRefinersForPG(
      {
        id: pgId,
        query: state[params.GLOBAL_FILTER_PERFORMANCE_GROUP_QUERY],
      },
      PGDefaultFilter,
      config.refinerKeys[0],
    ),
  );
  if (!!config.refinerKeys[1]) {
    uri.push(
      ...getFilterAndSearchRefinersForPG(
        {
          id: state[params.GLOBAL_FILTER_DEST_PERFORMANCE_GROUP_ID],
          query: state[params.GLOBAL_FILTER_DEST_PERFORMANCE_GROUP_QUERY],
        },
        '',
        config.refinerKeys[1],
      ),
    );
  }
  /* #endregion */

  /* #region campaigns */
  uri.push(
    ...getFilterAndSearchRefinersForCampaign(
      {
        id: state[params.GLOBAL_FILTER_CAMPAIGN_ID],
        query: state[params.GLOBAL_FILTER_CAMPAIGN_QUERY],
      },
      campaignsDefaultFilter,
      config.refinerKeys[0],
    ),
  );
  if (!!config.refinerKeys[1]) {
    uri.push(
      ...getFilterAndSearchRefinersForCampaign(
        {
          id: state[params.GLOBAL_FILTER_DEST_CAMPAIGN_ID],
          query: state[params.GLOBAL_FILTER_DEST_CAMPAIGN_QUERY],
        },
        '',
        config.refinerKeys[1],
      ),
    );
  }
  /* #endregion */

  /* #region ad groups */

  if (config.includeAdGroup) {
    uri.push(
      ...getFilterAndSearchRefinersForAdgroup(
        {
          id: state[params.GLOBAL_FILTER_AD_GROUPS_ID],
          query: state[params.GLOBAL_FILTER_AD_GROUPS_QUERY],
        },
        adGroupsDefaultFilter,
        config.refinerKeys[0],
      ),
    );
    if (!!config.refinerKeys[1]) {
      uri.push(
        ...getFilterAndSearchRefinersForAdgroup(
          {
            id: state[params.GLOBAL_FILTER_DEST_AD_GROUPS_ID],
            query: state[params.GLOBAL_FILTER_DEST_AD_GROUPS_QUERY],
          },
          '',
          config.refinerKeys[1],
        ),
      );
    }
  }
  /* #endregion */

  /* #region criteria */
  if (config.includeAdGroup && config.includeCriteria) {
    if (
      typeof state[params.GLOBAL_FILTER_CRITERIA_ID] !== 'string' &&
      typeof state[params.GLOBAL_FILTER_CRITERIA_QUERY] !== 'string'
    )
      uri.push(...extractCriteriaRefiners(criteriaDefaultFilter));
    else {
      if (state[params.GLOBAL_FILTER_CRITERIA_ID])
        uri.push(['filter_adspert_criterion_id', state[params.GLOBAL_FILTER_CRITERIA_ID]]);
      else if (state[params.GLOBAL_FILTER_CRITERIA_QUERY])
        uri.push(...extractCriteriaRefiners(state[params.GLOBAL_FILTER_CRITERIA_QUERY]));
    }
  }
  /* #endregion */

  if (Object.keys(columnsMappings).length) {
    uri.forEach((d) => {
      if (columnsMappings[d[0]]) d[0] = columnsMappings[d[0]];
    });
  }

  if (config.forApiMutator) {
    return <refiners>{
      filterValues: convertArrayRefinersToApiFormat(uri, 'filter'),
      searchValues: convertArrayRefinersToApiFormat(uri, 'search'),
    };
  }

  return uri;
};

export const FilterTypes = {
  multi: 'multi-select',
  single: 'single-select',
  switch: 'switch',
  query: 'query',
  queryMultiple: 'queryMultiple',
  compare: 'compare',
};

export const GetDateFilters = (daterange: string): [string, string | number][] => {
  const [start, end] = <[string, string]>GetDateRangeWithoutTimeZone(daterange, true);
  return [
    ['filter_date_ge', start],
    ['filter_date_le', end],
  ];
};

export const extractAccountRefiners = (data) => {
  return extractRefiners(data, {
    platform: 'filter_account_platform',
    optimize: 'filter_account_optimize',
    tags: 'filter_account_tags_user',
  });
};
export const extractPGRefiners = (data, targetKey: RefinerKeys) => {
  return extractRefiners(data, {
    enabled: `filter_${!!targetKey ? targetKey + '_' : ''}performance_group_enabled`,
    optiType: `filter_${!!targetKey ? targetKey + '_' : ''}performance_group_optimization_type`,
    goalType: `filter_${!!targetKey ? targetKey + '_' : ''}performance_group_goal_types`,
    campaignsCount: `filter_${!!targetKey ? targetKey + '_' : ''}campaigns_count`,
  });
};
export const extractCampaignsRefiners = (data, targetKey: RefinerKeys) => {
  return extractRefiners(data, {
    ras: `filter_${!!targetKey ? targetKey + '_' : ''}campaign_amz_ras_campaign`,
    status: `filter_${!!targetKey ? targetKey + '_' : ''}campaign_status`,
    optimize: `filter_${!!targetKey ? targetKey + '_' : ''}campaign_optimize`,
    optiType: `filter_${!!targetKey ? targetKey + '_' : ''}campaign_optimization_type`,
    type: `filter_${!!targetKey ? targetKey + '_' : ''}campaign_type`,
    name: `search_${!!targetKey ? targetKey + '_' : ''}campaign_name`,
    portfolio: `filter_${!!targetKey ? targetKey + '_' : ''}adspert_portfolio_id`,
    label: `filter_${!!targetKey ? targetKey + '_' : ''}campaign_labels`,
    ebayMarketplace: `filter_${!!targetKey ? targetKey + '_' : ''}campaign_eby_marketplace_id`,
  });
};
export const extractAdgroupsRefiners = (data, targetKey: RefinerKeys) => {
  return extractRefiners(data, {
    status: `filter_${!!targetKey ? targetKey + '_' : ''}adgroup_status`,
    name: `search_${!!targetKey ? targetKey + '_' : ''}adgroup_name`,
    retailers: `filter_${!!targetKey ? targetKey + '_' : ''}adgroup_amz_retailer_id`,
  });
};
export const extractCriteriaRefiners = (data) => {
  return extractRefiners(data, {
    status: 'filter_criterion_status',
    name: 'search_criterion_name',
    matchType: 'filter_criterion_match_type',
    type: 'filter_criterion_type',
  });
};

export const extractRulesRefiners = (d) =>
  extractRefiners(d, {
    ruleInherited: 'filter_rule_inherited',
    ruleAction: 'filter_rule_action',
    ruleTargetingType: 'filter_rule_targeting_type',
    ruleMatchType: 'filter_rule_match_type',
    ruleNegative: 'filter_rule_negative',
    ruleProductAdSyncMode: 'filter_rule_product_ad_sync_mode',
  });

export const getPortfolioLabelFromParams = (queryparams, key: 'portfolio' | 'label'): null | string => {
  const query = queryparams[params.GLOBAL_FILTER_CAMPAIGN_QUERY];
  if (!query || !query.includes(key)) return null;

  const ids = query
    .split(';')
    .map((item) => splitCompositeSubFilter(item))
    .find(([k, v]) => k.toLowerCase() === key)?.[1];

  return ids.split(',')[0];
};

const extractRefiners = (data, keys: Record<string, string>): URLs => {
  const uri = [];
  data
    .split(';')
    .filter((d) => !d.toUpperCase().endsWith(':ALL'))
    .forEach((param) => {
      const [paramKey, value] = splitCompositeSubFilter(param);
      const selectedCompareRefiner = CompareRefiners[paramKey];
      if (selectedCompareRefiner) {
        Object.keys(keys)
          .filter((key) => key === paramKey)
          .forEach((key) => {
            selectedCompareRefiner[value].map((e) => uri.push([keys[key] + e.key, e.value]));
          });
      } else {
        Object.keys(keys)
          .filter((key) => key === paramKey)
          .forEach((key) => uri.push([keys[key], value]));
      }
    });
  return uri;
};

export function convertArrayRefinersToApiFormat(uri: URLs, key) {
  const dt = {};
  uri.filter((d) => d[0].startsWith(key)).forEach((d) => (dt[snakeToCamelCase(d[0].replace(`${key}_`, ''))] = d[1]));
  return <refiners>dt;
}

function getFilterAndSearchRefinersForAdgroup(
  { id, query }: { id: string; query: string },
  defaultFilter: string,
  targetKey: RefinerKeys,
) {
  const uri: URLs = [];
  if (typeof id !== 'string' && typeof query !== 'string')
    uri.push(...extractAdgroupsRefiners(defaultFilter, targetKey));
  else {
    if (id) uri.push([`filter_${!!targetKey ? targetKey + '_' : ''}adspert_adgroup_id`, id]);
    else if (query) uri.push(...extractAdgroupsRefiners(query, targetKey));
  }

  return uri;
}

function getFilterAndSearchRefinersForCampaign(
  { id, query }: { id: string; query: string },
  defaultFilter: string,
  targetKey: RefinerKeys,
) {
  const uri: URLs = [];
  if (typeof id !== 'string' && typeof query !== 'string') {
    uri.push(...extractCampaignsRefiners(defaultFilter, targetKey));
  } else {
    if (id) uri.push([`filter_${!!targetKey ? targetKey + '_' : ''}adspert_campaign_id`, id]);
    else if (query) uri.push(...extractCampaignsRefiners(query, targetKey));
  }
  return uri;
}

function getFilterAndSearchRefinersForPG(
  { id, query }: { id: string; query: string },
  defaultFilter: string,
  targetKey: RefinerKeys,
) {
  const uri: URLs = [];
  if (typeof id !== 'string' && typeof query !== 'string') uri.push(...extractPGRefiners(defaultFilter, targetKey));
  else {
    if (id) uri.push([`filter_${!!targetKey ? targetKey + '_' : ''}performance_group_id`, id]);
    else if (query) uri.push(...extractPGRefiners(query, targetKey));
  }
  return uri;
}

export function getTransformedRefinerKey(key: string, settings: { delay: boolean; lessThan7: boolean }): string {
  if (!key) return key;
  return getLessThan7DaysRefinerKey(getDelayRefinerKey(key, settings.delay), settings.lessThan7);
}

export function getDelayRefinerKey(key: string, delay: boolean = true): string {
  if (!delay) return key;

  return DelayMappedMetrics[key] || key;
}

export function transformDelayObject(obj: object, delay: boolean = true): object {
  if (!delay) return obj;

  const data = {};
  Object.keys(obj).forEach((key) => (data[key] = obj[getDelayRefinerKey(key, delay)]));

  return data;
}

export function getLessThan7DaysRefinerKey(key: string, lessThan7: boolean = true): string {
  if (!lessThan7) return key;

  return LessThan7DaysMappedMetrics[key] || key;
}

export function GetMetricTypeInfoArray(
  keys: AllMetrics[],
  delay: boolean = false,
  lessThan7: boolean = false,
): MetricTypeInfo[] {
  return keys.map((key) => GetMetricTypeInfo(key, delay, lessThan7));
}

export function GetMetricTypeInfo(key: AllMetrics, delay: boolean = false, lessThan7: boolean = false): MetricTypeInfo {
  let type;

  if (currencyMetrics.includes(key)) type = MetricType.currency;
  else if (percentMetrics.includes(key)) type = MetricType.percent;
  else if (numberMetrics.includes(key)) type = MetricType.number;
  else alert(key);

  return { type, key: <AllMetrics>getTransformedRefinerKey(key, { delay, lessThan7 }) };
}

export function convertObjectForDelay<T extends object>(obj: T, delay: boolean = true, lessThan7 = false): T {
  if (!delay) {
    return obj;
  }

  return Object.keys(obj).reduce(
    (a, key) => ({
      ...a,
      [key]: obj[getTransformedRefinerKey(key, { delay, lessThan7 })],
    }),
    {} as T,
  );
}

export function getNextSortStep(sort: string): 'ascend' | 'descend' | null {
  if (sort === 'ascend') return 'descend';
  if (sort === 'descend') return null;
  return 'ascend';
}

export interface MultiSortAction {
  multiSort: boolean;
  sortProps: Record<string, 'descend' | 'ascend'>;
}

export function processSortAction(prevSort: MultiSortAction, currentSort: MultiSortAction): MultiSortAction {
  if (prevSort.multiSort !== currentSort.multiSort) {
    if (!currentSort.multiSort) {
      const [[key, value] = []] = Object.entries(prevSort.sortProps);

      if (key && value) {
        currentSort.sortProps = { [key]: value };
      }
    }
  } else {
    if (currentSort.multiSort) {
      currentSort.sortProps = { ...prevSort.sortProps, ...currentSort.sortProps };
    }
  }

  currentSort.sortProps = Object.entries(currentSort.sortProps).reduce(
    (a, [key, value]) => ({
      ...a,
      ...(value ? { [key]: value } : {}),
    }),
    {},
  );

  return currentSort;
}

export function stateHasDelay(state: Params): boolean {
  return state[params.DELAY] !== 'false';
}

export function getMetricFilterAPIParams(d: IMetricsSubFilter, addPrefix = true) {
  const params: { key: string; value: string }[] = [];
  const filters: { key: string; type: string; value: string; active?: boolean }[] = [];
  const prefix = addPrefix ? `${d.apiRefinerType}_` : '';

  let value = isGermanLocale() ? (<string>d.value)?.replace(',', '.') : d.value;
  let endRange = d.endRange;

  if (d.subType === 'percent') {
    value = value && (+value / 100).toString();
    endRange = endRange && (+endRange / 100).toString();
  }

  if (d.comparator === 'RANGE') {
    if (value && endRange) {
      filters.push({
        key: `${prefix}${d.columnName}_${d.comparator.toLowerCase()}`,
        type: d.type,
        value: `${value}^^${endRange}`,
      });
      params.push({
        key: d.key,
        value: `${d.comparator},${value}^^${endRange}`,
      });
    }
  } else {
    if (value) {
      filters.push({
        key: `${prefix}${d.columnName}_${d.comparator.toLowerCase()}`,
        type: d.type,
        value: value as string,
      });
      params.push({
        key: d.key,
        value: `${d.comparator},${value}`,
      });
    }
  }

  return { params, filters };
}

export function hasCampaignFilter(state: Params): boolean {
  return !(!state[params.GLOBAL_FILTER_CAMPAIGN_ID] && state[params.GLOBAL_FILTER_CAMPAIGN_QUERY] === undefined);
}

export function hasAdgroupFilter(state: Params): boolean {
  return !(!state[params.GLOBAL_FILTER_AD_GROUPS_ID] && state[params.GLOBAL_FILTER_AD_GROUPS_QUERY] === undefined);
}

export function getOptimizeFilter(key: string = 'optimize'): SubFilter {
  return {
    key,
    columnName: key,
    displayName: '',
    type: 'multi-select',
    apiRefinerType: 'filter',
    options: [
      { active: true, text: '', key: 'true' },
      { active: true, text: '', key: 'false' },
    ],
    allOptions: [],
  };
}

export function RemoveOptiTypeFilterForMonitor(value) {
  if (!value) {
    return value;
  }
  if (location.pathname.indexOf('monitor') !== -1) {
    // if monitor page remove filter
    return value
      .split(';')
      .filter((d) => d.indexOf(campaignFilters.optiType.key) === -1)
      .join(';');
  }
  return value;
}

function addEmptyTextKey(data: SubFilterOption[]) {
  return data.map((d) => ({ ...d, text: d?.text || '' }));
}

export function multiSelectFilter(
  key: string,
  columnName: string,
  allOptions: SubFilterOption[],
  displayName = '',
): SubFilter {
  return {
    key,
    columnName,
    displayName,
    type: 'multi-select',
    apiRefinerType: 'filter',
    options: addEmptyTextKey(allOptions),
    allOptions: addEmptyTextKey(allOptions),
  };
}
